import React, { useState, useEffect } from 'react';
import './ChallengesBox.css';

function ChallengesBox({ onViewAll }) {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date().getTime());

  useEffect(() => {
    fetchChallenges();
    // Refresh data every 30 seconds
    const dataInterval = setInterval(fetchChallenges, 30000);
    // Update countdown every second
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date().getTime());
    }, 1000);
    
    return () => {
      clearInterval(dataInterval);
      clearInterval(timeInterval);
    };
  }, []);

  const fetchChallenges = async () => {
    try {
      const response = await fetch('/backend/api/challenges/list.php?filter=all', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Filter to show accepting (max 2) and active challenges (max 3)
          const accepting = data.challenges.filter(c => c.status === 'accepting').slice(0, 2);
          const active = data.challenges.filter(c => c.status === 'active').slice(0, 3);
          setChallenges([...accepting, ...active]);
        }
      }
    } catch (error) {
      console.error('Error fetching challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTimeRemaining = (challenge) => {
    const deadlineTime = new Date(challenge.acceptance_deadline).getTime();
    const diff = deadlineTime - currentTime;
    
    // If challenge is active (not accepting), show "Active"
    if (challenge.status === 'active') {
      return 'Active';
    }
    
    // If challenge is completed or cancelled, show status
    if (challenge.status === 'completed') {
      return 'Completed';
    }
    
    if (challenge.status === 'cancelled') {
      return 'Cancelled';
    }
    
    // Only show countdown for accepting challenges
    if (diff <= 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleJoinChallenge = async (challengeId, e) => {
    e.stopPropagation();
    try {
      const response = await fetch('/backend/api/challenges/join.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_id: challengeId })
      });
      
      const data = await response.json();
      if (data.success) {
        fetchChallenges(); // Refresh
      } else {
        alert(data.error || 'Failed to join challenge');
      }
    } catch (error) {
      console.error('Error joining challenge:', error);
      alert('Failed to join challenge');
    }
  };

  if (loading) {
    return (
      <div className="challenges-box">
        <div className="challenges-header">
          <h3>Challenges</h3>
        </div>
        <div className="challenges-loading">Loading...</div>
      </div>
    );
  }

  if (challenges.length === 0) {
    return (
      <div className="challenges-box">
      <div className="challenges-header">
        <h3>Challenges</h3>
        <button className="btn-view-all" onClick={() => onViewAll('challenges')}>View All</button>
      </div>
        <div className="challenges-empty">
          <p>No active challenges</p>
          <p className="empty-subtitle">Create one to get started!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="challenges-box">
      <div className="challenges-header">
        <h3>Challenges</h3>
        <button className="btn-view-all" onClick={() => onViewAll('challenges')}>View All</button>
      </div>
      
      <div className="challenges-list">
        {challenges.map(challenge => (
          <div 
            key={challenge.id} 
            className="challenge-widget-row"
            onClick={() => onViewAll('challenge-detail', challenge.id)}
          >
            <div className="widget-row-left">
              <div className="widget-title">{challenge.title}</div>
              <div className="widget-status">
                {challenge.status === 'accepting' && (
                  <span className="status-accepting">⏰ {getTimeRemaining(challenge)}</span>
                )}
                {challenge.status === 'active' && (
                  <span className="status-active">🔥 Active</span>
                )}
                {challenge.status === 'completed' && (
                  <span className="status-completed">✓ Completed</span>
                )}
                {challenge.status === 'cancelled' && (
                  <span className="status-cancelled">❌ Cancelled</span>
                )}
              </div>
            </div>
            
            <div className="widget-row-right">
              <div className="widget-participants">👥 {challenge.participant_count}</div>
              {challenge.user_is_participant > 0 && (
                <span className="widget-joined">✓</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ChallengesBox;

